// Date answers in an event form are kept per field: { [fieldId]: 'yyyy-mm-dd' }.
// A form can hold more than one date field (Startdatum + Einddatum), so a single
// shared date value would make the fields mirror each other and drop all but one
// answer on save.

/**
 * @typedef {Object.<string, string>} DatesByFieldId
 * Date input values keyed by EventField ID. Empty string = not filled in.
 */

/**
 * The date that represents the moment of the event itself: the first date field
 * the user filled in, in form order (Startdatum before Einddatum).
 *
 * @param {DatesByFieldId} datesByFieldId - Date values keyed by field ID
 * @param {string[]} orderedFieldIds - Date field IDs in form order (EventFields sorted by Position)
 * @returns {string} The chosen date, or '' when no date field is filled
 */
export const pickEventDate = (datesByFieldId, orderedFieldIds) => {
    const dates = datesByFieldId || {}
    const ids = Array.isArray(orderedFieldIds) ? orderedFieldIds : []

    const filled = ids.find(id => dates[id])

    return filled ? dates[filled] : ''
}

/**
 * @typedef {object} DateResult
 * @property {string} FieldID - EventField the date belongs to
 * @property {string} Value - The date value as entered ('yyyy-mm-dd')
 * @property {null} OptionID - Always null; a date has no answer option
 * @property {'date'} Type - Result type
 */

/**
 * One EventResults doc per filled date field. The caller adds the `ID`.
 *
 * @param {DatesByFieldId} datesByFieldId - Date values keyed by field ID
 * @param {object} baseMeta - Shared doc fields (tenant, event, session, user, timestamp)
 * @returns {Array<DateResult & object>} One result per filled date field
 */
export const buildDateResults = (datesByFieldId, baseMeta) => {
    return Object.entries(datesByFieldId || {})
        .filter(([, value]) => Boolean(value))
        .map(([fieldId, value]) => ({
            ...baseMeta,
            FieldID: fieldId,
            Value: value,
            OptionID: null,
            Type: 'date'
        }))
}
